/**
 * Las etiquetas de una oferta («verano», «oferta», «nuevo») y, en productos
 * físicos y digitales, su marca (Nike, Apple).
 *
 * No se enseñan en el sitio: sirven para las reglas de las colecciones
 * automáticas («las que tengan la etiqueta verano»). Se guardan al momento,
 * como un interruptor: añadir o quitar una es una decisión suelta. Los dos
 * campos son los del blog: se elige entre las que ya hay o se crea otra.
 */
import { useEffect, useState } from 'react'
import { IconTag } from '@tabler/icons-react'
import { OfferCard } from './OfferCard'
import { OfferBrandField, OfferTagsField } from './OfferBrandField'
import type { KnownOption } from './BlogPickerField'
import { updateOffer, type Offer } from '../../../lib/api'
import { useToast } from '../../Toast'
import { translateServerError, useI18n } from '../../../lib/i18n'

export function OfferTagsCard({ projectId, token, offer, onChange, tags: knownTagList = [], brands = [], onTagRemoved, onBrandRemoved }: {
  projectId: string
  token: string
  offer: Offer
  onChange: (offer: Offer) => void
  /** Las etiquetas que ya usan las ofertas, con cuántas las llevan. */
  tags?: KnownOption[]
  /** Las marcas que ya usan las ofertas, con cuántas las llevan. */
  brands?: KnownOption[]
  onTagRemoved?: (name: string) => void
  onBrandRemoved?: (name: string) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [tags, setTags] = useState<string[]>(offer.tags ?? [])
  const [saving, setSaving] = useState(false)
  useEffect(() => { setTags(offer.tags ?? []) }, [offer.tags])
  const hasBrand = offer.type === 'physical' || offer.type === 'digital'

  const save = async (patch: { tags?: string[]; brand?: string }) => {
    if (patch.tags) setTags(patch.tags)
    setSaving(true)
    try {
      onChange(await updateOffer(projectId, offer.id, patch, token))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
      setTags(offer.tags ?? [])
    } finally {
      setSaving(false)
    }
  }

  return (
    <OfferCard icon={<IconTag size={15} />} title={t(hasBrand ? 'offers.tags.titleWithBrand' : 'offers.tags.title')} hint={saving ? t('common.saving') : undefined}>
      {hasBrand ? (
        <div className="dash-offer-brand">
          <span>{t('offers.brand.label')}</span>
          <OfferBrandField
            projectId={projectId}
            token={token}
            value={offer.brand ?? ''}
            known={brands}
            onChange={(next) => { if (next !== (offer.brand ?? '')) void save({ brand: next }) }}
            {...(onBrandRemoved ? { onRemovedEverywhere: onBrandRemoved } : {})}
          />
        </div>
      ) : null}
      <div className="dash-offer-brand">
        <span>{t('offers.tags.title')}</span>
        <OfferTagsField
          projectId={projectId}
          token={token}
          value={tags}
          known={knownTagList}
          onChange={(next) => { void save({ tags: next }) }}
          {...(onTagRemoved ? { onRemovedEverywhere: onTagRemoved } : {})}
        />
      </div>
      <p className="dash-card-description">{t('offers.tags.body')}</p>
    </OfferCard>
  )
}
